import { useCallback, useEffect, useRef, useState } from 'react';

/** Client bound under server 15s intake ceiling (REQ-LEARN-013). */
export const WEB_DRILL_MAX_RECORDING_MS = 12_000;

export type DrillRecordingPhase =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'stopping'
  | 'denied'
  | 'unavailable';

export interface UseDrillRecordingOptions {
  /**
   * Fired when the max-duration timer stops capture (not manual stop / cancel).
   * Receives the same Blob the manual `stop()` path would resolve with.
   */
  onAutoStop?: (blob: Blob | null) => void;
}

export interface UseDrillRecordingResult {
  phase: DrillRecordingPhase;
  /** True while MediaRecorder is capturing. */
  isRecording: boolean;
  errorCode: 'permission-denied' | 'unavailable' | 'empty' | null;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  /** Cancel capture and release mic tracks (navigation / unmount). */
  cancel: () => void;
}

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') {
    return 'audio/webm';
  }
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
    return 'audio/webm;codecs=opus';
  }
  if (MediaRecorder.isTypeSupported('audio/webm')) {
    return 'audio/webm';
  }
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
    return 'audio/ogg;codecs=opus';
  }
  return '';
}

/**
 * Bounded browser recording for listen-and-repeat (REQ-MOB-019).
 * Mic permission is requested only inside `start()` (user gesture).
 */
export function useDrillRecording(
  maxDurationMs: number = WEB_DRILL_MAX_RECORDING_MS,
  options: UseDrillRecordingOptions = {}
): UseDrillRecordingResult {
  const [phase, setPhase] = useState<DrillRecordingPhase>('idle');
  const [errorCode, setErrorCode] = useState<UseDrillRecordingResult['errorCode']>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const stopResolverRef = useRef<((blob: Blob | null) => void) | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  /** Guards concurrent start() while awaiting getUserMedia (phase state lags). */
  const startInFlightRef = useRef(false);
  const onAutoStopRef = useRef(options.onAutoStop);
  onAutoStopRef.current = options.onAutoStop;

  const clearMaxTimer = useCallback(() => {
    if (maxTimerRef.current !== null) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    clearMaxTimer();
    startInFlightRef.current = false;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      } catch {
        // already stopped
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    if (stopResolverRef.current) {
      stopResolverRef.current(null);
      stopResolverRef.current = null;
    }
    releaseStream();
    if (!disposedRef.current) {
      // Denial is sticky only as an error signal; start() may re-request permission.
      setPhase(current => (current === 'denied' ? 'denied' : 'idle'));
    }
  }, [clearMaxTimer, releaseStream]);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      cancel();
    };
  }, [cancel]);

  const start = useCallback(async () => {
    if (disposedRef.current) return;
    if (startInFlightRef.current) return;
    if (phase === 'recording' || phase === 'requesting' || phase === 'stopping') return;

    startInFlightRef.current = true;
    setErrorCode(null);

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPhase('unavailable');
      setErrorCode('unavailable');
      startInFlightRef.current = false;
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setPhase('unavailable');
      setErrorCode('unavailable');
      startInFlightRef.current = false;
      return;
    }

    setPhase('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (disposedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        startInFlightRef.current = false;
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearMaxTimer();
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob =
          chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type: blobType }) : null;
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        releaseStream();
        const resolve = stopResolverRef.current;
        stopResolverRef.current = null;
        // Fail closed on zero chunks / zero-byte audio — never upload empty capture.
        const result = blob && blob.size > 0 ? blob : null;
        if (!disposedRef.current) {
          if (result === null) {
            setErrorCode('empty');
          }
          setPhase('idle');
        }
        // Manual stop installs a resolver; max-duration auto-stop uses onAutoStop.
        if (resolve) {
          resolve(result);
        } else if (!disposedRef.current) {
          onAutoStopRef.current?.(result);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setPhase('recording');
      startInFlightRef.current = false;

      maxTimerRef.current = setTimeout(() => {
        const active = mediaRecorderRef.current;
        if (!active || active.state !== 'recording') return;
        // Auto-stop: no stopResolver yet — onstop delivers via onAutoStop.
        setPhase('stopping');
        try {
          active.stop();
        } catch {
          if (!disposedRef.current) {
            setPhase('idle');
            onAutoStopRef.current?.(null);
          }
        }
      }, maxDurationMs);
    } catch (error) {
      releaseStream();
      if (disposedRef.current) {
        startInFlightRef.current = false;
        return;
      }
      const name =
        error && typeof error === 'object' && 'name' in error
          ? String((error as { name: unknown }).name)
          : '';
      if (
        name === 'NotAllowedError' ||
        name === 'PermissionDeniedError' ||
        name === 'SecurityError'
      ) {
        setPhase('denied');
        setErrorCode('permission-denied');
      } else {
        setPhase('unavailable');
        setErrorCode('unavailable');
      }
      startInFlightRef.current = false;
    }
  }, [clearMaxTimer, maxDurationMs, phase, releaseStream]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return null;
    }
    setPhase('stopping');
    clearMaxTimer();
    return new Promise<Blob | null>(resolve => {
      stopResolverRef.current = resolve;
      try {
        recorder.stop();
      } catch {
        stopResolverRef.current = null;
        releaseStream();
        setPhase('idle');
        resolve(null);
      }
    });
  }, [clearMaxTimer, releaseStream]);

  return {
    phase,
    isRecording: phase === 'recording',
    errorCode,
    start,
    stop,
    cancel,
  };
}
